import { Component } from '@theme/component';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
import { preloadImage } from '@theme/utilities';

/**
 * Full-screen PDP image zoom modal powered by Swiper.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends Component<Refs>
 */
export class SwiperZoomDialog extends Component {
  requiredRefs = ['dialog'];

  /** @type {import('swiper').default | null} */
  #mainSwiper = null;

  /** @type {import('swiper').default | null} */
  #thumbsSwiper = null;

  /** @type {boolean} */
  #initialized = false;

  /** @type {Set<string>} */
  #highResImagesLoaded = new Set();

  /** @type {number} */
  #previousScrollY = 0;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('close', this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog.removeEventListener('close', this.#handleDialogClose);

    if (this.refs.dialog.open) {
      this.#unlockScroll();
    }

    this.#destroySwipers();
  }

  #handleDialogClose = () => {
    this.#unlockScroll();
    window.dispatchEvent(new DialogCloseEvent());
  };

  #lockScroll() {
    this.#previousScrollY = window.scrollY;
    document.documentElement.setAttribute('scroll-lock', '');
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${this.#previousScrollY}px`;
  }

  #unlockScroll() {
    document.documentElement.removeAttribute('scroll-lock');
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
  }

  /**
   * Opens the swiper zoom dialog at the given slide index.
   *
   * @param {number} index - The media index to show.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event.preventDefault();

    const { dialog } = this.refs;
    const isOpening = !dialog.open;

    if (isOpening) {
      this.#lockScroll();
      dialog.showModal();
    }

    await this.#initSwipers();

    if (!this.#mainSwiper) return;

    if (this.#mainSwiper.params.loop) {
      this.#mainSwiper.slideToLoop(index, 0);
    } else {
      this.#mainSwiper.slideTo(index, 0);
    }

    const activeIndex = this.#mainSwiper.realIndex ?? index;
    this.#loadHighResForSlide(activeIndex);
    this.#updateCounter(activeIndex);
    this.#dispatchSelected(activeIndex);
  }

  /**
   * Closes the swiper zoom dialog.
   */
  close() {
    this.refs.dialog.close();
  }

  /**
   * Closes the dialog when Escape is pressed.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Preloads the high-resolution image for a slide index.
   *
   * @param {number} index - The slide index.
   */
  preloadImage(index) {
    this.#loadHighResForSlide(index);
  }

  /**
   * Waits until Swiper is available on window.
   * @returns {Promise<typeof import('swiper').default | undefined>}
   */
  async #waitForSwiper() {
    if (window.Swiper) return window.Swiper;

    return new Promise((resolve) => {
      const check = () => {
        if (window.Swiper) {
          resolve(window.Swiper);
          return;
        }
        requestAnimationFrame(check);
      };
      check();
    });
  }

  async #initSwipers() {
    if (this.#initialized) return;

    const mainElement = this.querySelector('[data-swiper-zoom-main]');
    if (!mainElement) return;

    const SwiperConstructor = await this.#waitForSwiper();
    if (!SwiperConstructor) return;

    const thumbsElement = this.querySelector('[data-swiper-zoom-thumbs]');
    const paginationElement = this.querySelector('[data-swiper-zoom-pagination]');
    const slideCount = mainElement.querySelectorAll('[data-swiper-zoom-slide]').length;
    const enableLoop = slideCount > 1;

    if (thumbsElement) {
      this.#thumbsSwiper = new SwiperConstructor(thumbsElement, {
        spaceBetween: 4,
        slidesPerView: 'auto',
        freeMode: true,
        watchSlidesProgress: true,
      });
    }

    this.#mainSwiper = new SwiperConstructor(mainElement, {
      spaceBetween: 0,
      centeredSlides: true,
      loop: enableLoop,
      loopedSlides: slideCount,
      slidesPerView: 1,
      speed: 400,
      slideToClickedSlide: true,
      navigation: {
        nextEl: this.querySelector('[data-swiper-zoom-next]'),
        prevEl: this.querySelector('[data-swiper-zoom-prev]'),
      },
      pagination: paginationElement
        ? {
            el: paginationElement,
            type: 'progressbar',
          }
        : undefined,
      thumbs: this.#thumbsSwiper
        ? {
            swiper: this.#thumbsSwiper,
          }
        : undefined,
      breakpoints: {
        750: {
          spaceBetween: 0,
          slidesPerView: 2,
          loopedSlides: slideCount,
        },
      },
      on: {
        slideChange: (swiper) => {
          const index = swiper.realIndex;
          this.#loadHighResForSlide(index);
          this.#updateCounter(index);
          this.#dispatchSelected(index);
        },
      },
    });

    this.#initialized = true;
  }

  /**
   * Loads the highest resolution image for a slide.
   *
   * @param {number} index - The slide index.
   */
  #loadHighResForSlide(index) {
    const slide = this.querySelector(`[data-swiper-zoom-slide="${index}"]`);
    if (!slide) return;

    const image = slide.querySelector('img[data-max-resolution]');
    if (!(image instanceof HTMLImageElement)) return;

    const highResolutionUrl = image.getAttribute('data-max-resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return;

    preloadImage(highResolutionUrl);

    const highResImage = new Image();
    highResImage.className = image.className;
    highResImage.alt = image.alt;
    highResImage.setAttribute('data-max-resolution', highResolutionUrl);

    if (image.style.cssText) {
      highResImage.style.cssText = image.style.cssText;
    }

    highResImage.onload = () => {
      image.replaceWith(highResImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    highResImage.src = highResolutionUrl;
  }

  /**
   * @param {number} index
   */
  #updateCounter(index) {
    const counter = this.querySelector('[data-swiper-zoom-counter-current]');
    if (counter) {
      counter.textContent = String(index + 1);
    }
  }

  /**
   * @param {number} index
   */
  #dispatchSelected(index) {
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  #destroySwipers() {
    this.#mainSwiper?.destroy(true, true);
    this.#thumbsSwiper?.destroy(true, true);
    this.#mainSwiper = null;
    this.#thumbsSwiper = null;
    this.#initialized = false;
  }
}

if (!customElements.get('swiper-zoom-dialog')) {
  customElements.define('swiper-zoom-dialog', SwiperZoomDialog);
}
